import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { MarketplaceQuantityControl } from "./MarketplaceQuantityControl";
import { formatPriceBrl } from "../utils/addressHelpers";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import { marketplaceProductCardSx } from "../constants/marketplaceCardStyles";
import type { WooProduct } from "../types";
import {
  canIncreaseQuantity,
  getProductStockLimit,
  isLowStock,
} from "../utils/stockHelpers";

interface WooProductGridProps {
  products: WooProduct[];
  loading: boolean;
  error: string | null;
  onAddToCart: (product: WooProduct) => void;
  getItemQuantity?: (productId: number) => number;
  onUpdateQuantity?: (productId: number, quantity: number) => void;
}

export function WooProductGrid({
  products,
  loading,
  error,
  onAddToCart,
  getItemQuantity,
  onUpdateQuantity,
}: WooProductGridProps) {
  const theme = useTheme();

  if (loading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={32} />
        <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }} mt={1}>
          Carregando produtos...
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Typography sx={{ ...mt.body, color: "error.main" }} textAlign="center" py={2}>
        {error}
      </Typography>
    );
  }

  if (products.length === 0) {
    return (
      <Typography sx={{ ...mt.body, color: "text.secondary" }} textAlign="center" py={4}>
        Nenhum produto encontrado.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          sm: "repeat(3, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
          lg: "repeat(5, minmax(0, 1fr))",
        },
        gap: 1.25,
      }}
    >
      {products.map((product) => {
        const imageSrc =
          product.images[0]?.thumbnail ?? product.images[0]?.src ?? "";
        const displayPrice =
          product.onSale && product.salePrice
            ? product.salePrice
            : product.price || product.regularPrice;
        const outOfStock =
          product.stockStatus === "outofstock" || !product.purchasable;
        const qty = getItemQuantity?.(product.id) ?? 0;
        const inCart = qty > 0;
        const stockLimit = getProductStockLimit(product);
        const lowStock = isLowStock(product);

        return (
          <Stack
            key={product.id}
            sx={{
              width: "100%",
              overflow: "hidden",
              ...marketplaceProductCardSx(theme),
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  bgcolor: alpha(theme.palette.primary800.main, 0.04),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1,
                }}
              >
                {imageSrc ? (
                  <Box
                    component="img"
                    src={imageSrc}
                    alt={product.name}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
                    Sem imagem
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ position: "absolute", top: 8, left: 8 }}>
                {product.onSale ? (
                  <Chip label="Promoção" size="small" color="secondary" sx={{ height: 22 }} />
                ) : null}
                {lowStock ? (
                  <Chip label="Últimas unidades" size="small" color="warning" sx={{ height: 22 }} />
                ) : null}
              </Stack>
            </Box>

            <Stack spacing={0.75} sx={{ p: 1.25, flex: 1, minHeight: 88 }}>
              <Typography
                sx={{
                  ...mt.bodySecondary,
                  fontWeight: 600,
                  minHeight: 40,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {product.name}
              </Typography>

              <Stack direction="row" spacing={0.5} alignItems="baseline" flexWrap="wrap">
                <Typography sx={{ ...mt.price, color: "primary800.main" }}>
                  {formatPriceBrl(displayPrice)}
                </Typography>
                {product.onSale && product.regularPrice ? (
                  <Typography
                    sx={{
                      ...mt.bodySecondary,
                      color: "text.secondary",
                      textDecoration: "line-through",
                    }}
                  >
                    {formatPriceBrl(product.regularPrice)}
                  </Typography>
                ) : null}
              </Stack>

              {inCart && onUpdateQuantity ? (
                <Box
                  sx={{
                    mt: "auto",
                    p: 0.75,
                    borderRadius: 1.5,
                    bgcolor: "action.hover",
                  }}
                >
                  <MarketplaceQuantityControl
                    quantity={qty}
                    stockLimit={stockLimit}
                    canIncrease={canIncreaseQuantity(product, qty)}
                    onChange={(nextQty) => onUpdateQuantity(product.id, nextQty)}
                  />
                </Box>
              ) : (
                <Button
                  variant="contained"
                  disabled={outOfStock}
                  onClick={() => onAddToCart(product)}
                  sx={{
                    mt: "auto",
                    minHeight: 40,
                    ...mt.button,
                    textTransform: "none",
                    bgcolor: "primary800.main",
                    "&:hover": { bgcolor: "primary900.main" },
                  }}
                >
                  {outOfStock ? "Indisponível" : "Adicionar"}
                </Button>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Box>
  );
}
